/**
 * Everything a catalogue search box needs for suggestions, apart from how they look:
 * the debounced request, the combined book-then-author list, and the combobox wiring.
 * The public Catalog (SMCHS styles) and the staff Books page (--lib-* styles) render
 * the list their own way.
 */
import { useState } from 'react';
import type React from 'react';
import { useBookSuggestions } from '@/hooks/catalog-hooks';
import { useCombobox } from '@/hooks/use-combobox';
import { useDebounce } from '@/hooks/use-debounce';
import type { AuthorSuggestion, BookSuggestion } from '@/services/catalog-api';

export type Suggestion =
  | { kind: 'book'; book: BookSuggestion }
  | { kind: 'author'; author: AuthorSuggestion };

export function useBookSuggestionsBox(opts: {
  text: string;
  onPick: (suggestion: Suggestion) => void;
  /** False turns suggestions off (e.g. a 12-digit barcode on the staff page). */
  enabled?: boolean;
}) {
  const { text, onPick, enabled = true } = opts;
  const [open, setOpen] = useState(false);
  const [debounced] = useDebounce(text.trim(), 150);
  const wanted = enabled && debounced.length >= 2 ? debounced : '';
  const { data } = useBookSuggestions(wanted);

  const items: Suggestion[] =
    wanted && data
      ? [
          ...data.books.map((book) => ({ kind: 'book' as const, book })),
          ...data.authors.map((author) => ({ kind: 'author' as const, author })),
        ]
      : [];

  const box = useCombobox<Suggestion>({
    items,
    resetKey: data?.query,
    open: open && !!wanted,
    onOpenChange: setOpen,
    onPick: (suggestion) => {
      setOpen(false);
      onPick(suggestion);
    },
  });

  return {
    ...box,
    items,
    /** What the list was fetched for, for "No matches for ..." and the live region. */
    query: wanted,
    books: data && wanted ? data.books : [],
    authors: data && wanted ? data.authors : [],
    inputProps: {
      ...box.inputProps,
      onFocus: () => setOpen(true),
      onBlur: () => setOpen(false),
    },
    /** The list is wanted (open, 2+ characters), even if it has nothing to show. */
    open: open && !!wanted,
    /** Call from the input's onChange: typing reopens a list closed with Escape. */
    reopen: () => setOpen(true),
    close: () => setOpen(false),
    /** Fetched and empty: show "No matches". */
    empty: !!wanted && !!data && data.query === wanted && items.length === 0,
  } as const;
}

export type BookSuggestionsBox = ReturnType<typeof useBookSuggestionsBox>;
export type OptionProps = ReturnType<BookSuggestionsBox['optionProps']> & { key?: React.Key };
